// SceltaFiltro.tsx — la scelta del filtro tra oltre 400: ricerca per nome, categorie, icona di ogni filtro.
// Quelli usabili si scelgono; gli altri mostrano come averli (Silver o crediti). Nomi e descrizioni arrivano da un
// file per lingua con tutti i filtri (filtri/indice, generato da scripts/genera-indice-filtri.mjs).
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { filtroAdatto, impostaPesi, pesoFiltro, telefonoLento, type VoceIndice } from "../filtri/potenza.ts";
import { filtriDisponibili, profiloFiltri, type FiltroDisponibile } from "../servizi/filtri.ts";
import { accessoFiltro, ordinaPerScelta, type Accesso } from "../filtri/accesso.ts";
import { testiFiltro } from "../filtri/registro.ts";
import { IconaFiltro } from "../componenti/IconaFiltro.tsx";
import { CATEGORIE_FILTRO as CATEGORIE, type CategoriaFiltro as Categoria } from "../filtri/tipi.ts";

interface Voce extends FiltroDisponibile {
  nome: string;
  descrizione: string;
  accesso: Accesso;
  categoria: Categoria | null;
  icona: boolean;
}

interface Props {
  attivo: string | null;
  onScegli: (id: string | null) => void;
}

type Gruppo = "tutti" | "usabili" | Categoria;

/** Confronto senza maiuscole e accenti («caffè» trova «caffe»). */
const pulisci = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();

export function SceltaFiltro({ attivo, onScegli }: Props) {
  const { t, i18n } = useTranslation();
  const [voci, setVoci] = useState<Voce[] | null>(null);
  const [errore, setErrore] = useState(false);
  const [bloccato, setBloccato] = useState<Voce | null>(null);
  const [cerca, setCerca] = useState("");
  const [gruppo, setGruppo] = useState<Gruppo>("tutti");

  useEffect(() => {
    let chiuso = false;
    Promise.all([filtriDisponibili(), import("../filtri/indice/indice.json")])
      .then(async ([filtri, indice]) => {
        const info = new Map((indice.default as unknown as { id: string; categoria: Categoria; icona: boolean }[]).map((x) => [x.id, x]));
        const profilo = profiloFiltri();
        impostaPesi(indice.default as unknown as VoceIndice[]);
        // v046: sui telefoni più lenti, prima i filtri più fluidi (stesso ordine, ma i «pesanti» in fondo)
        const lento = telefonoLento();
        const ordinati = ordinaPerScelta(filtri, profilo)
          .map((f, i) => ({ f, i }))
          .sort((a, b) => (lento ? Number(!filtroAdatto(a.f.id)) - Number(!filtroAdatto(b.f.id)) : 0) || a.i - b.i)
          .map((x) => x.f);
        const testi = await Promise.all(ordinati.map((f) => testiFiltro(f.id, i18n.language)));
        if (!chiuso)
          setVoci(
            ordinati.map((f, i) => ({
              ...f,
              ...testi[i]!,
              accesso: accessoFiltro(f, profilo),
              categoria: info.get(f.id)?.categoria ?? null,
              icona: info.get(f.id)?.icona ?? false,
            })),
          );
      })
      .catch(() => !chiuso && setErrore(true));
    return () => {
      chiuso = true;
    };
  }, [i18n.language]);

  const categorie = useMemo(() => CATEGORIE.filter((c) => voci?.some((v) => v.categoria === c)), [voci]);
  const visibili = useMemo(() => {
    if (!voci) return [];
    const q = pulisci(cerca.trim());
    return voci.filter(
      (v) =>
        (gruppo === "tutti" || (gruppo === "usabili" ? v.accesso === "libero" : v.categoria === gruppo)) &&
        (!q || pulisci(v.nome).includes(q) || pulisci(v.descrizione).includes(q)),
    );
  }, [voci, cerca, gruppo]);

  if (errore) return <p className="nota" role="alert">{t("gioco.filtri_errore_catalogo")}</p>;
  if (!voci) return <p className="nota" aria-busy="true">{t("generale.caricamento")}</p>;

  const usabili = voci.filter((v) => v.accesso === "libero").length;
  const lento = telefonoLento();
  return (
    <div className="scelta-filtro">
      <input
        type="search"
        className="campo campo-cerca-filtro"
        value={cerca}
        placeholder={t("filtri.cerca")}
        aria-label={t("filtri.cerca")}
        onChange={(e) => setCerca(e.target.value)}
      />
      <div className="gruppi-filtri" role="tablist" aria-label={t("filtri.categorie")}>
        {(["tutti", "usabili", ...categorie] as Gruppo[]).map((g) => (
          <button key={g} type="button" role="tab" aria-selected={gruppo === g} className="chip" onClick={() => setGruppo(g)}>
            {g === "tutti" ? t("filtri.tutti", { n: voci.length }) : g === "usabili" ? t("filtri.usabili", { n: usabili }) : t(`filtri.categoria_${g}`)}
          </button>
        ))}
      </div>
      <div className="griglia-filtri" role="radiogroup" aria-label={t("gioco.filtro")}>
        {!cerca && (gruppo === "tutti" || gruppo === "usabili") && (
          <button type="button" role="radio" aria-checked={attivo === null} className="carta-filtro" onClick={() => onScegli(null)}>
            <span className="carta-filtro-segno" aria-hidden="true">
              ∅
            </span>
            <span className="carta-filtro-nome">{t("gioco.filtro_nessuno")}</span>
          </button>
        )}
        {visibili.map((v) => (
          <button
            key={v.id}
            type="button"
            role="radio"
            aria-checked={attivo === v.id}
            className={`carta-filtro ${v.accesso !== "libero" ? "carta-filtro-chiusa" : ""}`}
            title={v.descrizione}
            onClick={() => (v.accesso === "libero" ? onScegli(v.id) : setBloccato(v))}
          >
            <IconaFiltro id={v.id} nome={v.nome} dimensione={64} />
            <span className="carta-filtro-nome">{v.nome}</span>
            {lento && pesoFiltro(v.id) === "leggero" && <span className="ld-fluido">⚡ {t("filtri.fluido")}</span>}
            {v.accesso === "silver" && <span className="carta-filtro-etichetta">{t("gioco.filtro_silver")}</span>}
            {v.accesso === "crediti" && <span className="carta-filtro-etichetta">{t("gioco.filtro_crediti", { n: v.prezzoCrediti })}</span>}
          </button>
        ))}
        {visibili.length === 0 && <p className="nota">{t("filtri.nessun_risultato")}</p>}
      </div>
      {bloccato && (
        <p className="nota" role="status">
          {bloccato.descrizione} {t(bloccato.accesso === "silver" ? "gioco.filtro_serve_silver" : "gioco.filtro_serve_crediti", { n: bloccato.prezzoCrediti })}
        </p>
      )}
    </div>
  );
}
